import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ChevronDown, AlertTriangle, ThumbsDown, 
  TrendingDown, Eye, Users
} from 'lucide-react';

// Floating Sackocap Particles Component
const FloatingSackocapParticles = React.memo(({ count = 6 }) => {
  const particles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      delay: i * 1500,
      duration: 7000 + (i * 800),
      size: 22 + (i * 3), // Bigger sizes: 22px to 37px
      initialRotation: i * 60,
      // Random positioning to avoid diagonal line
      leftPosition: Math.random() * 80 + 10, // Random between 10% and 90%
      topPosition: Math.random() * 80 + 10,  // Random between 10% and 90%
    }))
  , [count]);

  return (
    <>
      <style>{`
        @keyframes float0 {
          0% { transform: translateY(0px) translateX(0px) rotate(0deg); }
          25% { transform: translateY(-25px) translateX(15px) rotate(90deg); }
          50% { transform: translateY(-15px) translateX(-10px) rotate(180deg); }
          75% { transform: translateY(-30px) translateX(20px) rotate(270deg); }
          100% { transform: translateY(0px) translateX(0px) rotate(360deg); }
        }
        
        @keyframes float1 {
          0% { transform: translateY(-15px) translateX(10px) rotate(45deg); }
          25% { transform: translateY(-35px) translateX(-15px) rotate(135deg); }
          50% { transform: translateY(-10px) translateX(25px) rotate(225deg); }
          75% { transform: translateY(-40px) translateX(-20px) rotate(315deg); }
          100% { transform: translateY(-15px) translateX(10px) rotate(405deg); }
        }
        
        @keyframes float2 {
          0% { transform: translateY(-10px) translateX(-15px) rotate(90deg); }
          25% { transform: translateY(-20px) translateX(25px) rotate(180deg); }
          50% { transform: translateY(-35px) translateX(-10px) rotate(270deg); }
          75% { transform: translateY(-15px) translateX(30px) rotate(360deg); }
          100% { transform: translateY(-10px) translateX(-15px) rotate(450deg); }
        }
        
        @keyframes float3 {
          0% { transform: translateY(-20px) translateX(-10px) rotate(120deg); }
          25% { transform: translateY(-30px) translateX(20px) rotate(210deg); }
          50% { transform: translateY(-10px) translateX(-20px) rotate(300deg); }
          75% { transform: translateY(-35px) translateX(15px) rotate(390deg); }
          100% { transform: translateY(-20px) translateX(-10px) rotate(480deg); }
        }
        
        @keyframes float4 {
          0% { transform: translateY(-5px) translateX(20px) rotate(180deg); }
          25% { transform: translateY(-25px) translateX(-10px) rotate(270deg); }
          50% { transform: translateY(-40px) translateX(15px) rotate(360deg); }
          75% { transform: translateY(-20px) translateX(-25px) rotate(450deg); }
          100% { transform: translateY(-5px) translateX(20px) rotate(540deg); }
        }
        
        @keyframes float5 {
          0% { transform: translateY(-25px) translateX(5px) rotate(240deg); }
          25% { transform: translateY(-10px) translateX(-20px) rotate(330deg); }
          50% { transform: translateY(-30px) translateX(25px) rotate(420deg); }
          75% { transform: translateY(-45px) translateX(-15px) rotate(510deg); }
          100% { transform: translateY(-25px) translateX(5px) rotate(600deg); }
        }
        
        @keyframes float6 {
          0% { transform: translateY(-30px) translateX(-25px) rotate(300deg); }
          25% { transform: translateY(-15px) translateX(30px) rotate(390deg); }
          50% { transform: translateY(-35px) translateX(-5px) rotate(480deg); }
          75% { transform: translateY(-50px) translateX(20px) rotate(570deg); }
          100% { transform: translateY(-30px) translateX(-25px) rotate(660deg); }
        }
        
        @keyframes particlePulse {
          0% { opacity: 0.8; transform: scale(0.9); }
          100% { opacity: 1.0; transform: scale(1.1); }
        }
      `}</style>
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute pointer-events-none"
            style={{
              left: `${particle.leftPosition}%`,
              top: `${particle.topPosition}%`,
              animation: `float${particle.id} ${particle.duration}ms infinite linear`,
              animationDelay: `${particle.delay}ms`,
            }}
          >
            <img
              src="/sackocap.png"
              alt=""
              className="object-contain pointer-events-none"
              style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                filter: 'drop-shadow(0 3px 12px rgba(220, 38, 38, 0.5))',
                animation: `particlePulse 2.5s ease-in-out infinite alternate`,
                animationDelay: `${particle.delay * 0.2}ms`,
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ))}
      </div>
    </>
  );
});

// Optimized Sackocap Icon Component
const SackocapIcon = React.memo(({ className = "h-6 w-6", fadeIn = false, clickable = false, onClick = null }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);
  
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);
  
  const handleClick = useCallback(() => {
    if (clickable && onClick) {
      onClick();
    }
  }, [clickable, onClick]);
  
  if (imageError) {
    return (
      <div className={`flex items-center justify-center ${className} ${clickable ? 'cursor-pointer hover:scale-110 transition-transform duration-300' : ''}`} onClick={handleClick}>
        <span className="text-2xl">💩</span>
      </div>
    );
  }
  
  return (
    <div className={`relative ${className} ${clickable ? 'cursor-pointer hover:scale-110 transition-transform duration-300' : ''}`} onClick={handleClick}>
      <img
        src="/sackocap.png"
        alt="Sacko Cap"
        className={`w-full h-full object-contain transition-all duration-1000 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        } ${fadeIn && imageLoaded ? 'animate-pulse' : ''}`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
      />
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg">💩</span>
        </div>
      )}
      {clickable && imageLoaded && (
        <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-300 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100">
          <Eye className="h-6 w-6 text-white" />
        </div>
      )}
    </div>
  );
});

// Optimized Winner Avatar Component with memoization
const WinnerAvatar = React.memo(({ 
  winnerName, 
  className = "", 
  fallbackIcon: FallbackIcon = Users,
  size = "small"
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const imageSrc = `/${winnerName.toLowerCase()}happy.png`;
  
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);
  
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);
  
  // Memoized size mappings
  const sizeConfig = useMemo(() => ({
    small: { container: "w-10 h-10", icon: "h-5 w-5" },
    medium: { container: "w-16 h-16", icon: "h-8 w-8" },
    large: { container: "w-20 h-20", icon: "h-10 w-10" }
  }[size]), [size]);
  
  if (imageError || !imageSrc) {
    return (
      <div className={`bg-green-500/30 rounded-full flex items-center justify-center border-3 border-green-400/50 shadow-lg ${sizeConfig.container} ${className}`}>
        <FallbackIcon className={`${sizeConfig.icon} text-green-400`} />
      </div>
    );
  }
  
  return (
    <div className={`relative rounded-full overflow-hidden border-3 border-green-400/60 shadow-xl ${sizeConfig.container} ${className}`}>
      <img
        src={imageSrc}
        alt={`${winnerName} happy avatar`}
        className={`w-full h-full object-cover transition-all duration-300 hover:scale-110 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
      />
      {!imageLoaded && (
        <div className="absolute inset-0 bg-green-500/20 rounded-full flex items-center justify-center">
          <FallbackIcon className={`${sizeConfig.icon} text-green-400`} />
        </div>
      )}
      <div className="absolute inset-0 rounded-full ring-2 ring-green-400/30 ring-offset-1 ring-offset-transparent"></div>
    </div>
  );
});

const HallOfSackos = ({ h2hData, finalData, draftData, legacyData, loading, error }) => {
  const [isHeaderAnimated, setIsHeaderAnimated] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Optimized modal handlers
  const handleSackoCapClick = useCallback(() => {
    setFullscreenImage({ 
      src: '/sackocap.png', 
      alt: 'Sacko Cap - Symbol of Fantasy Football Shame' 
    });
    document.body.style.overflow = 'hidden';
  }, []);

  const handleImageClick = useCallback((src, alt) => {
    setFullscreenImage({ src, alt });
    document.body.style.overflow = 'hidden';
  }, []);

  const handleCloseModal = useCallback(() => {
    setFullscreenImage(null);
    document.body.style.overflow = 'unset';
  }, []);

  // Memoized sacko data to prevent unnecessary re-renders
  const sackoData = useMemo(() => [
    {
      year: 2025,
      sacko: "Kevin",
      team: "Obi-Jan Kenobi & Quon Solo",
      bigGame: "Kevin loses to Karp, 102.96 - 103.40",
      winner: "Karp",
      lvp: "Kareem Hunt 0.20 pts (proj 6.96)",
      margin: -0.44,
      isRecent: true,
      sackoNumber: 1,
      customImageSrc: "/kevinsacko1.png"
    },
    {
      year: 2024,
      sacko: "Joey",
      team: "The Adams Family",
      bigGame: "Joey loses to Nipples, 55.80 - 94.66",
      winner: "Nipples",
      lvp: "Brian Robinson Jr. -0.10 pts (proj 10.57)",
      margin: -38.86,
      isRecent: false,
      sackoNumber: 2
    },
    {
      year: 2023,
      sacko: "Joey",
      team: "The Adams Family", 
      bigGame: "Joey loses to Jake 82.52 - 79.06",
      winner: "Jake",
      lvp: "Davante Adams -0.40 pts (proj 9.53)",
      margin: -3.46,
      isRecent: true,
      sackoNumber: 1
    },
    {
      year: 2022,
      sacko: "Jake",
      team: "The Olegnatarfs",
      bigGame: "Jake loses to Kevin 93.46 - 77.96", 
      winner: "Kevin",
      lvp: "DEN D -4 pts (proj 7.54)",
      margin: -15.50,
      isRecent: true,
      sackoNumber: 3
    },
    {
      year: 2021,
      sacko: "Lyons",
      team: "Hodors Hodors",
      bigGame: "Lyons loses to Karp 85.76 - 93.68",
      winner: "Karp",
      lvp: "Tyler Johnson -0 pts (proj 5.56)",
      margin: -7.92,
      sackoNumber: 1
    },
    {
      year: 2020,
      sacko: "Jake", 
      team: "The Olegnatarfs",
      bigGame: "Jake loses to Dan 75.40 - 115.92",
      winner: "Dan",
      lvp: "JuJu Smith-Schuster -0.50 pts (proj 9.11)",
      margin: -40.52,
      sackoNumber: 2
    },
    {
      year: 2019,
      sacko: "Scott",
      team: "Jumbo's Bonecrushers", 
      bigGame: "Scott loses to Mills 78.68 - 88.90",
      winner: "Mills",
      lvp: "Robert Woods -1.70 pts (proj 10.06)",
      margin: -10.22,
      sackoNumber: 2
    },
    {
      year: 2018,
      sacko: "Raffy",
      team: "NOT IMPRESSED",
      bigGame: "Raffy loses to Scott 62.02 - 64.56",
      winner: "Scott",
      lvp: "Drew Brees -6.92 pts (proj 20.68)",
      margin: -2.54,
      sackoNumber: 2
    },
    {
      year: 2017,
      sacko: "Raffy",
      team: "NOT IMPRESSED",
      bigGame: "Raffy loses to Shuds 61.58 - 95.94", 
      winner: "Shuds",
      lvp: "Leonard Fournette -0 pts (proj 0)",
      margin: -34.36,
      sackoNumber: 1
    },
    {
      year: 2016,
      sacko: "Scott",
      team: "Jumbo's Bonecrushers",
      bigGame: "Scott loses to Mills 94.94 - 104.32",
      winner: "Mills",
      lvp: "Darren Sproles -0 pts (proj 0.16)",
      margin: -9.38,
      sackoNumber: 1
    },
    {
      year: 2015,
      sacko: "Sam",
      team: "The Abusement Park",
      bigGame: "Sam loses to Scott 85.54 - 97.60",
      winner: "Scott",
      lvp: "Taiwan Jones -0 pts (proj 0.75)",
      margin: -12.06,
      sackoNumber: 2
    },
    {
      year: 2014,
      sacko: "Mills",
      team: "Perfectville", 
      bigGame: "Mills loses to Joey 78.68 - 101.98",
      winner: "Joey",
      lvp: "Keenan Allen -1.80 pts (proj 9.30)",
      margin: -23.30,
      sackoNumber: 1
    },
    {
      year: 2013,
      sacko: "Sam",
      team: "The Abusement Park",
      bigGame: "Sam loses to Mills 68.30 - 96.48",
      winner: "Mills",
      lvp: "Christian Ponder -0 pts (proj 0.08)",
      margin: -28.18,
      sackoNumber: 1
    },
    {
      year: 2012,
      sacko: "Jake",
      team: "Griffin's Gryffons",
      bigGame: "Jake loses to Scott 62.24 - 83.15",
      winner: "Scott",
      lvp: "Ahmad Bradshaw -0 pts (proj 0)",
      margin: -20.91,
      sackoNumber: 1
    }
  ], []);

  // Optimized helper functions with useCallback
  const getOrdinalSuffix = useCallback((num) => {
    const suffixes = ["th", "st", "nd", "rd"];
    const value = num % 100;
    return num + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
  }, []);

  const getPrimaryChampionImageSrc = useCallback((championName, sackoNumber, customImageSrc = null) => {
    if (customImageSrc) {
      return customImageSrc;
    }
    const cleanName = championName.toLowerCase().replace(/\s+/g, '');
    if (sackoNumber === 1) {
      return `/${cleanName}anguished.png`;
    } else {
      return `/${cleanName}sacko${sackoNumber}.png`;
    }
  }, []);

  const getAngryChampionImageSrc = useCallback((championName) => {
    const filename = championName.toLowerCase().replace(/\s+/g, '') + 'angry.png';
    return `/${filename}`;
  }, []);

  const getLvpImageSrc = useCallback((year) => {
    return `/${year}lvp.webp`;
  }, []);

  const getSackoImageSrc = useCallback((year) => {
    return `/${year}sacko.webp`;
  }, []);

  // Header animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHeaderAnimated(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Cleanup overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Optimized Modal Component with performance improvements
  const FullscreenModal = useMemo(() => {
    if (!fullscreenImage) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4"
        onClick={handleCloseModal}
      >
        <div className="relative max-w-7xl max-h-[95vh] w-full">
          <button
            onClick={handleCloseModal}
            className="absolute -top-16 right-0 text-white hover:text-gray-300 text-3xl font-bold bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center z-10"
            aria-label="Close modal"
          >
            ×
          </button>
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-2">
            <img
              src={fullscreenImage.src}
              alt={fullscreenImage.alt}
              className="w-full h-full object-contain rounded-xl shadow-2xl max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    );
  }, [fullscreenImage, handleCloseModal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-red-200 text-xl">Loading Hall of Shame...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-black flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-32 w-32 text-red-500 mx-auto mb-4" />
          <p className="text-red-200 text-xl">Error loading shame data: {error}</p>
        </div>
      </div>
    );
  }

  // Premium SackoCard with mobile optimization
  const SackoCard = React.memo(({ sacko, index }) => {
    const isHovered = hoveredCard === sacko.year;
    const is2025 = sacko.year === 2025;
    
    const handleMouseEnter = useCallback(() => {
      setHoveredCard(sacko.year);
    }, [sacko.year]);
    
    const handleMouseLeave = useCallback(() => {
      setHoveredCard(null);
    }, []);
    
    return (
      <div className="w-full">
        <div
          className={`group relative overflow-hidden rounded-3xl transition-all duration-700 w-full ${
            isHovered ? 'scale-[1.02]' : 'scale-100'
          }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            animationDelay: `${index * 150}ms`,
            filter: isHovered ? 'brightness(1.1) saturate(1.2)' : 'brightness(1)',
            boxShadow: isHovered 
              ? '0 40px 80px rgba(0,0,0,0.6), 0 0 120px rgba(220,38,38,0.4)' 
              : '0 20px 40px rgba(0,0,0,0.4)',
          }}
        >
          <div className="relative h-auto min-h-[600px] w-full overflow-visible">
            {/* Luxurious gradient background */}
            <div className={`absolute inset-0 ${
              sacko.isRecent || is2025
                ? 'bg-gradient-to-br from-red-600 via-red-700 to-red-900' 
                : 'bg-gradient-to-br from-gray-800 via-gray-900 to-black'
            }`}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-black/10 to-transparent"></div>
            </div>

            {/* Angry Champion Background Image - Subtle */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] opacity-12">
                <img 
                  src={getAngryChampionImageSrc(sacko.sacko)}
                  alt={`${sacko.sacko} Background`}
                  className="w-full h-full object-contain object-center filter blur-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Premium Mobile Layout with proper out-of-bounds effect */}
            <div className="block lg:hidden relative p-6 sm:p-8 overflow-visible">
              <div className="space-y-6 overflow-visible">
                {/* Mobile Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <SackocapIcon 
                      className="h-10 w-10 drop-shadow-lg" 
                      clickable={true}
                      onClick={handleSackoCapClick}
                    />
                    <div>
                      <div className="text-3xl font-black text-white drop-shadow-lg tracking-tight">
                        {sacko.year}
                      </div>
                      {is2025 ? (
                        <div className="bg-red-600/25 backdrop-blur-sm rounded-full px-3 py-1 border border-red-500/30 inline-block mt-1">
                          <span className="text-xs font-black text-red-200 tracking-wider">LATEST</span>
                        </div>
                      ) : (
                        <div className="text-red-300/90 text-sm font-semibold tracking-wide uppercase">
                          Sacko
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Screenshot */}
                  <button
                    className="w-16 h-12 rounded-xl overflow-hidden shadow-xl border-2 border-red-500/40 hover:scale-105 transition-transform duration-300 relative bg-black flex-shrink-0"
                    onClick={() => handleImageClick(`/${sacko.year}sacko.png`, `${sacko.year} Sacko Shame`)}
                    aria-label={`View ${sacko.year} Sacko Shame`}
                  >
                    <img 
                      src={getSackoImageSrc(sacko.year)}
                      alt={`${sacko.year} Sacko Shame`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.target.src.includes('.webp')) {
                          e.target.src = `/${sacko.year}sacko.png`;
                        }
                      }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Eye className="h-3 w-3 text-white opacity-80" />
                    </div>
                  </button>
                </div>

                {/* Mobile Sacko Section with True Out-of-Bounds Effect */}
                <div className="relative overflow-visible">
                  {/* Background card - this creates the "container" */}
                  <div className="bg-black/30 backdrop-blur-xl rounded-2xl p-6 border border-red-500/30 shadow-2xl overflow-visible">
                    <div className="flex flex-col items-center text-center space-y-4 overflow-visible">
                      {/* Sacko image - simple centered */}
                      <div className="relative -mt-12 mb-2 overflow-visible flex justify-center">
                        {/* Floating Particles - positioned over this specific image area */}
                        <FloatingSackocapParticles count={is2025 ? 7 : 6} />
                        
                        <img
                          src={getPrimaryChampionImageSrc(sacko.sacko, sacko.sackoNumber, sacko.customImageSrc)}
                          alt={`${sacko.sacko} Anguished Photo`}
                          className="w-44 h-44 sm:w-52 sm:h-52 object-contain drop-shadow-2xl relative z-20"
                          style={{
                            filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.4))'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                          loading="lazy"
                        />
                        <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-3xl shadow-2xl bg-gradient-to-br from-red-400/80 to-red-600/80 backdrop-blur-sm flex items-center justify-center relative z-20" style={{display: 'none'}}>
                          <SackocapIcon 
                            className="h-20 w-20 sm:h-24 sm:w-24 drop-shadow-2xl" 
                            clickable={true}
                            onClick={handleSackoCapClick}
                          />
                        </div>
                        
                        {/* Enhanced glow effect */}
                        <div className="absolute inset-0 bg-gradient-radial from-red-400/30 via-red-500/20 to-transparent rounded-full blur-2xl scale-150 -z-10"></div>
                      </div>
                      
                      <div className="space-y-3 mt-4">
                        <h3 className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg">
                          {sacko.sacko}
                        </h3>
                        <p className="text-red-200/95 text-xl sm:text-2xl font-medium italic">
                          {sacko.team}
                        </p>
                        <div className="bg-red-800/20 backdrop-blur-sm rounded-full px-5 py-3 inline-block border border-red-600/30">
                          <span className="text-red-200 font-bold text-base flex items-center">
                            <ThumbsDown className="h-4 w-4 mr-2" />
                            {getOrdinalSuffix(sacko.sackoNumber)} Sacko
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Game & LVP Grid - Compact */}
                <div className="grid grid-cols-1 gap-4">
                  {/* Sacko Game */}
                  <div className="bg-black/30 backdrop-blur-xl rounded-2xl p-5 border border-red-500/30 shadow-2xl">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-red-800/40 rounded-xl p-2 shadow-lg">
                        <TrendingDown className="h-5 w-5 text-red-400" />
                      </div>
                      <h4 className="text-red-300 font-bold text-base tracking-wide uppercase">
                        Sacko Game
                      </h4>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-red-200/80 font-medium text-sm">Lost to</span>
                        <div className="flex items-center space-x-2">
                          <WinnerAvatar 
                            winnerName={sacko.winner} 
                            size="small"
                            className="shadow-lg hover:shadow-green-500/50 transition-all duration-300"
                          />
                          <span className="text-green-200 font-black text-base">{sacko.winner}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-red-200/80 font-medium text-sm">Game</span>
                        <span className="text-red-200 font-bold text-sm text-right">{sacko.bigGame}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-red-200/80 font-medium text-sm">Margin</span>
                        <div className="bg-red-600/25 backdrop-blur-sm border border-red-500/40 text-red-100 font-bold px-3 py-1 rounded-full text-sm">
                          {sacko.margin.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* LVP */}
                  <div className="bg-black/30 backdrop-blur-xl rounded-2xl p-5 border border-red-500/30 shadow-2xl">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-red-800/40 rounded-xl p-2 shadow-lg">
                        <ThumbsDown className="h-5 w-5 text-red-400" />
                      </div>
                      <h4 className="text-red-300 font-bold text-base tracking-wide uppercase">
                        Least Valuable Player
                      </h4>
                    </div>

                    <div className="flex items-center space-x-3">
                        <button 
                        className="relative w-12 h-12 rounded-lg overflow-hidden shadow-lg border-2 border-red-500/30 flex-shrink-0 hover:scale-105 transition-transform duration-300 group"
                        onClick={() => handleImageClick(getLvpImageSrc(sacko.year), `${sacko.lvp.split(' ')[0]} ${sacko.lvp.split(' ')[1]} - ${sacko.year} LVP`)}
                        aria-label={`View ${sacko.year} LVP moment`}
                      >
                        <img 
                          src={getLvpImageSrc(sacko.year)}
                          alt={`${sacko.year} LVP Moment`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            if (e.target.src.includes('.webp')) {
                              e.target.src = `/${sacko.year}lvp.png`;
                            } else if (e.target.src.includes('.png')) {
                              e.target.src = `/${sacko.year}lvp.jpg`;
                            } else {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }
                          }}
                          loading="lazy"
                        />
                        <div className="w-full h-full bg-gradient-to-br from-red-400/80 to-red-600/80 backdrop-blur-sm flex items-center justify-center rounded-lg" style={{display: 'none'}}>
                          <ThumbsDown className="h-4 w-4 text-white" />
                        </div>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Eye className="h-3 w-3 text-white" />
                        </div>
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-black text-sm sm:text-base mb-1 leading-tight">
                          {sacko.lvp}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Desktop Layout */}
            <div className="hidden lg:block relative p-8 lg:p-12 overflow-visible">
              <div className="grid grid-cols-12 gap-8 h-full min-h-[500px] overflow-visible">
                {/* Left: Sacko Showcase with Out-of-Bounds */}
                <div className="col-span-4 flex flex-col justify-center items-center text-center overflow-visible">
                  {/* Sacko image - simple centered */}
                  <div className="mb-8 relative -mt-16 overflow-visible flex justify-center">
                    {/* Floating Particles - positioned over this specific image area */}
                    <FloatingSackocapParticles count={is2025 ? 7 : 6} />
                    
                    <img
                      src={getPrimaryChampionImageSrc(sacko.sacko, sacko.sackoNumber, sacko.customImageSrc)}
                      alt={`${sacko.sacko} Anguished Photo`}
                      className="w-80 h-80 object-contain drop-shadow-2xl relative z-20"
                      style={{
                        filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.4))'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                      loading="lazy"
                    />
                    <div className="w-80 h-80 rounded-3xl shadow-2xl bg-gradient-to-br from-red-400/80 to-red-600/80 backdrop-blur-sm flex items-center justify-center relative z-20" style={{display: 'none'}}>
                      <SackocapIcon 
                        className="h-32 w-32 drop-shadow-2xl" 
                        clickable={true}
                        onClick={handleSackoCapClick}
                      />
                    </div>
                    
                    {/* Enhanced glow effect */}
                    <div className="absolute inset-0 bg-gradient-radial from-red-400/30 via-red-500/20 to-transparent rounded-full blur-3xl scale-150 -z-10"></div>
                  </div>

                  <div>
                    <h3 className="text-5xl font-black text-white mb-3 drop-shadow-xl tracking-tight">
                      {sacko.sacko}
                    </h3>
                    <p className="text-red-200/95 text-2xl font-medium italic mb-4 tracking-wide">
                      {sacko.team}
                    </p>
                    <div className="bg-red-800/20 backdrop-blur-sm rounded-full px-6 py-3 inline-block border border-red-600/30 mb-4">
                      <span className="text-red-200 font-bold text-lg flex items-center">
                        <ThumbsDown className="h-5 w-5 mr-3" />
                        {getOrdinalSuffix(sacko.sackoNumber)} Sacko
                      </span>
                    </div>
                    
                    {is2025 ? (
                      <div className="bg-red-600/25 backdrop-blur-sm rounded-full px-6 py-3 border border-red-500/30 inline-block">
                        <span className="text-sm font-black text-red-200 tracking-wider">LATEST SACKO</span>
                      </div>
                    ) : sacko.isRecent && (
                      <div className="bg-red-600/25 backdrop-blur-sm rounded-full px-6 py-3 border border-red-500/30 inline-block">
                        <span className="text-sm font-black text-red-200 tracking-wider">RECENT</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Center: Year & Game Details */}
                <div className="col-span-4 flex flex-col justify-center space-y-8">
                  {/* Year Header */}
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-6 mb-6">
                      <div className="relative bg-black/40 backdrop-blur-xl rounded-3xl p-6 border border-red-500/40 shadow-2xl">
                        <SackocapIcon 
                          className="h-20 w-20 drop-shadow-lg" 
                          clickable={true}
                          onClick={handleSackoCapClick}
                        />
                        <div className="absolute inset-0 bg-gradient-radial from-red-400/30 via-red-500/20 to-transparent rounded-3xl blur-lg scale-125 pointer-events-none"></div>
                      </div>
                    </div>
                    <div className="text-6xl font-black text-white drop-shadow-xl tracking-tight mb-2">
                      {sacko.year}
                    </div>
                    <div className="text-red-300/90 text-xl font-semibold tracking-wide uppercase">
                      Sacko
                    </div>
                  </div>

                  {/* Game Screenshot */}
                  <button
                    className="w-full h-40 rounded-2xl overflow-hidden shadow-2xl border-3 border-red-500/50 hover:scale-105 transition-all duration-300 relative bg-black group"
                    onClick={() => handleImageClick(`/${sacko.year}sacko.png`, `${sacko.year} Sacko Shame`)}
                    aria-label={`View ${sacko.year} Sacko Shame`}
                  >
                    <img 
                      src={getSackoImageSrc(sacko.year)}
                      alt={`${sacko.year} Sacko Shame`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.target.src.includes('.webp')) {
                          e.target.src = `/${sacko.year}sacko.png`;
                        }
                      }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-red-600/25 backdrop-blur-sm rounded-2xl p-4 border border-red-500/30">
                        <Eye className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-white text-lg font-bold bg-black/70 px-4 py-3 rounded-xl backdrop-blur-sm text-center border border-red-500/20">
                        Click to View Shame
                      </div>
                    </div>
                  </button>

                  {/* Sacko Game Details */}
                  <div className="bg-black/30 backdrop-blur-xl rounded-2xl p-6 border border-red-500/30 shadow-2xl">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="bg-red-800/40 rounded-xl p-4 shadow-lg">
                        <TrendingDown className="h-8 w-8 text-red-400" />
                      </div>
                      <h4 className="text-red-300 text-xl font-bold tracking-wide uppercase">
                        Sacko Game
                      </h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-center space-x-6 mb-4">
                        <WinnerAvatar 
                          winnerName={sacko.winner} 
                          size="large"
                          className="shadow-2xl hover:shadow-green-500/50 transition-all duration-300"
                        />
                        <span className="text-red-200 font-black text-3xl">Lost to</span>
                        <span className="text-green-200 font-black text-2xl">{sacko.winner}</span>
                      </div>

                      <div className="text-center">
                        <div className="text-red-200 font-black text-lg mb-3">{sacko.bigGame}</div>
                        <div className="bg-red-600/25 backdrop-blur-sm border border-red-500/40 text-red-100 font-bold px-6 py-3 rounded-full inline-block">
                          Loss Margin: {sacko.margin.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: LVP Showcase */}
                <div className="col-span-4 flex flex-col justify-center space-y-6">
                  {/* LVP Image - Clickable */}
                  <button 
                    className="mb-6 h-56 rounded-2xl overflow-hidden shadow-2xl border-3 border-red-500/40 relative w-full hover:scale-105 transition-transform duration-300 group"
                    onClick={() => handleImageClick(getLvpImageSrc(sacko.year), `${sacko.lvp.split(' ')[0]} ${sacko.lvp.split(' ')[1]} - ${sacko.year} LVP`)}
                    aria-label={`View ${sacko.year} LVP moment`}
                  >
                    <img 
                      src={getLvpImageSrc(sacko.year)}
                      alt={`${sacko.year} LVP Moment`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.target.src.includes('.webp')) {
                          e.target.src = `/${sacko.year}lvp.png`;
                        } else if (e.target.src.includes('.png')) {
                          e.target.src = `/${sacko.year}lvp.jpg`;
                        } else {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }
                      }}
                      loading="lazy"
                    />
                    <div className="w-full h-full bg-gradient-to-br from-red-400/80 to-red-600/80 backdrop-blur-sm flex items-center justify-center" style={{display: 'none'}}>
                      <ThumbsDown className="h-16 w-16 text-white" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="bg-black/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-red-500/20">
                        <span className="text-white text-lg font-bold">Least Valuable Player</span>
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-red-600/25 backdrop-blur-sm rounded-2xl p-4 border border-red-500/30">
                        <Eye className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </button>

                  {/* LVP Details */}
                  <div className="bg-black/30 backdrop-blur-xl rounded-2xl p-6 border border-red-500/30 shadow-2xl text-center">
                    <div className="flex items-center justify-center space-x-4 mb-4">
                      <div className="bg-red-800/40 rounded-xl p-4 shadow-lg">
                        <ThumbsDown className="h-8 w-8 text-red-400" />
                      </div>
                      <h4 className="text-red-300 text-xl font-bold tracking-wide uppercase">
                        Least Valuable Player
                      </h4>
                    </div>
                    
                    <div>
                      <p className="text-white font-black text-2xl lg:text-3xl mb-3 leading-tight">
                        {sacko.lvp}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-red-500/20 blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-black/15 blur-2xl animate-pulse"></div>
              <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-red-400/50 rounded-full animate-bounce"></div>
              <div className="absolute top-3/4 right-1/3 w-2 h-2 bg-orange-500/60 rounded-full animate-bounce" style={{animationDelay: '700ms'}}></div>
            </div>

            {/* Premium Bottom Accent */}
            <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-red-800/60 via-red-600/80 to-red-800/60"></div>
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-black">
      {/* Premium Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-red-900 to-black">
          <div className="absolute top-20 left-1/4 w-32 h-32 bg-red-500/5 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 right-1/4 w-24 h-24 bg-orange-600/10 rounded-full blur-lg animate-bounce"></div>
          <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-red-700/10 rounded-full blur-md animate-pulse" style={{animationDelay: '300ms'}}></div>
        </div>

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto">
            <div className={`text-center mb-12 transform transition-all duration-1000 ${
              isHeaderAnimated ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className={`transform transition-all duration-1500 ${
                    isHeaderAnimated ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                  }`}>
                    <SackocapIcon 
                      className="h-48 w-48 sm:h-52 sm:w-52 lg:h-56 lg:w-56 drop-shadow-2xl" 
                      fadeIn={true} 
                      clickable={true}
                      onClick={handleSackoCapClick}
                    />
                  </div>
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 leading-tight">
                <span className="bg-gradient-to-r from-red-500 via-red-400 to-orange-500 bg-clip-text text-transparent">
                  Hall of Sackos
                </span>
              </h1>

              <p className="text-lg sm:text-xl lg:text-2xl text-red-100 font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
                Documenting the failures, disappointments, and embarrassments of the 
                <span className="text-red-400 font-bold"> Beasts of Bellevue</span>
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-900 via-black/80 to-transparent"></div>
      </div>

      <div className="text-center py-8">
        <div className="inline-flex items-center space-x-2 text-red-400 animate-bounce">
          <ChevronDown className="h-5 w-5" />
          <span className="text-sm font-medium">Scroll to witness the shame</span>
          <ChevronDown className="h-5 w-5" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Beast Jester Section */}
        <div className="text-center mb-16">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-red-600/30 rounded-full blur-2xl animate-pulse transform scale-110"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-yellow-400/20 via-orange-400/30 to-red-400/20 rounded-full blur-2xl animate-pulse delay-500 transform scale-125"></div>
            
            <div className="relative w-[14.4rem] h-[14.4rem] sm:w-64 sm:h-64 lg:w-[19.44rem] lg:h-[19.44rem] xl:w-[22.68rem] xl:h-[22.68rem] mx-auto">
              <img 
                src="/beastjester.png"
                alt="Beast Jester"
                className="w-full h-full object-contain"
                style={{
                  filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.6))',
                  maskImage: 'radial-gradient(circle, rgba(0,0,0,1) 70%, rgba(0,0,0,0.9) 85%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'radial-gradient(circle, rgba(0,0,0,1) 70%, rgba(0,0,0,0.9) 85%, rgba(0,0,0,0) 100%)',
                  transform: 'scale(1.6)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
                loading="lazy"
              />
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-600 to-red-800 rounded-full shadow-2xl" style={{display: 'none'}}>
                <TrendingDown className="h-16 w-16 text-white" />
              </div>
            </div>
          </div>
          <h2 className="text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            Failures Through the Ages
          </h2>
          <p className="text-red-200 text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
            Legends of disappointment, carved in shame
          </p>
        </div>

        {/* Sackos List */}
        <div className="space-y-8 lg:space-y-12">
          {sackoData.map((sacko, index) => (
            <SackoCard 
              key={sacko.year}
              sacko={sacko} 
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {FullscreenModal}

      <style jsx>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
    </div>
  );
};

export default HallOfSackos;